"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Logger = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _cloneDeep = _interopRequireDefault(require("clone-deep"));
var _deepmerge = _interopRequireDefault(require("deepmerge"));
var _nodePath = _interopRequireDefault(require("node:path"));
var _pino = require("pino");
var _pinoStdSerializers = _interopRequireDefault(require("pino-std-serializers"));
var _applicationInsights = require("./application-insights");
var _constants = require("./constants");
var _LoggerBrowserConfiguration = require("./internal/LoggerBrowserConfiguration");
var _formatError = require("./internal/formatError");
var _isPlainObject = require("./internal/isPlainObject");
var _loggerEventEmitter = require("./internal/loggerEventEmitter");
var _pinoBrowserRuntimeTransport = require("./internal/pino-runtime-transports/pinoBrowserRuntimeTransport");
var _printWarningWithTrace = require("./internal/printWarningWithTrace");
var _createMetadataTelemetryInitializer = require("./internal/telemetry/browser/createMetadataTelemetryInitializer");
var _SeverityLevels = require("./types/SeverityLevels");
var _Logger;
/* eslint-disable new-cap -- this is kind of enforced by Pino with the enum we have  */
const isInBrowserRuntime = typeof window !== 'undefined';
const isInNodeRuntime = !isInBrowserRuntime;
const loggerDebugIsEnabled = process.env.DEBUG_LOGGER === 'true';
const isTestingLogger = process.env.LOGGER_TEST === 'true';
const isDevelopment = process.env.NODE_ENV !== 'production';
const parentLoggerDefaultMessageMetadata = {};
const childLoggerDefaultMessageMetadata = {};

/**
 * Logger for Oriflame applications.
 *
 * @example
 *
 * #### Simple
 *
 * This will only print info to console:
 *
 * ```ts
 * // Setup should occur before any logging is done, but can be called after to update metadata globally
 * Logger.configure({
 *    // ...setup properties
 * });
 * // Create instance with desired name or any other configuration...
 * const myLogger = new Logger({ name: 'application' });
 * // ...and do the logging 🎉
 * myLogger.info('This is just info'); // => [application]: This is just info
 * // You can even attach useful information in object as a second parameter
 * myLogger.info('This is info with other information', { weather: '🌤️' }); // => [application]: This is info with other information { metadata: { weather:  '🌤️' } }
 * ```
 *
 * #### With metadata
 *
 * You can add something whats called 'metadata'. Either globally during setup or locally for Logger instance.
 * These metadata properties are added to each log output (formatting may differr)
 *
 * ```ts
 * // Add filters for your logging
 * Logger.updateConfiguration({
 *    filters: [
 *      forName: 'my-component',
 *      forSeverityLevel: SeverityLevels.DEBUG,
 *      // Let debug be always enabled for Logger with name 'my-component'
 *      enabled: true
 *   ]
 * })
 * // Create your instance
 * const myLogger = new Logger('my-component', { weather: '🌤️' });
 * // And log it to see the results
 * myLogger.info('Hello there'); // => [my-component]: Hello there { language: 'dk-DK', myProperty: { other: false }, metadata: { weather: '🌤️' } }
 * ```
 */
class Logger {
  constructor(name, loggerMetadataOrAreaName, loggerMetadata) {
    (0, _defineProperty2.default)(this, "name", void 0);
    (0, _defineProperty2.default)(this, "areaName", void 0);
    (0, _defineProperty2.default)(this, "pinoChild", null);
    (0, _defineProperty2.default)(this, "messageMetadata", (0, _cloneDeep.default)(childLoggerDefaultMessageMetadata));
    this.name = name;
    this.areaName = typeof loggerMetadataOrAreaName === 'string' ? loggerMetadataOrAreaName : undefined;
    this.messageMetadata = (typeof loggerMetadataOrAreaName === 'object' ? loggerMetadataOrAreaName : undefined) ?? loggerMetadata ?? childLoggerDefaultMessageMetadata;
  }

  // listen for localstorage updates without the need to reload the whole page
  /** Global logger localstorage listener */

  /**
   * Creates child Logger instance that describes more specific area
   * Is equivalent of `new Logger('<name>', <areaName>, ...)`
   */
  createChild(areaName, incommingChildMetadata = {}) {
    return new Logger(this.name, areaName,
    // Copy parent metadata
    (0, _deepmerge.default)(childLoggerDefaultMessageMetadata, incommingChildMetadata));
  }

  /**
   *  Gets global metadata. If executed in Node.js environment, it will also include metadata from AsyncLocalStorage.
   * @returns Global metadata
   */
  static get globalMetadata() {
    if (isInNodeRuntime) {
      // eslint-disable-next-line node/global-require, @typescript-eslint/no-require-imports -- caller is not async
      const {
        asyncLocalStorage
      } = require('./server/asyncLocalStorage');
      const storedMetadata = asyncLocalStorage.getStore();
      if (storedMetadata) {
        // if false, we have no defined store
        this._globalMetadata = storedMetadata;
      }
    }
    return (0, _deepmerge.default)(parentLoggerDefaultMessageMetadata, this._globalMetadata, {
      isMergeableObject: _isPlainObject.isPlainObject
    });
  }

  /**
   * Overrides global metadata with new set of metadata
   */
  static set globalMetadata(metadata) {
    this._globalMetadata = metadata;

    // If we are in Node.js environment we need to update AsyncLocalStorage as well
    if (isInNodeRuntime) {
      // eslint-disable-next-line node/global-require, @typescript-eslint/no-require-imports -- caller is not async
      const {
        asyncLocalStorage
      } = require('./server/asyncLocalStorage');
      const storedMetadata = asyncLocalStorage.getStore();
      if (storedMetadata) {
        // Assign metadata without changing the reference of the store object
        Object.assign(storedMetadata, this._globalMetadata);
      }
    }

    // If we are in browser and Application Insights is initialized, we need to update the metadata for dependencies
    if (isInBrowserRuntime && _applicationInsights.ApplicationInsights.isInitialized()) {
      this.metadataDependencyInitializer ??= (0, _createMetadataTelemetryInitializer.createMetadataTelemetryInitializer)(_applicationInsights.ApplicationInsights.getForClient());
      this.metadataDependencyInitializer(this._globalMetadata);
    }
  }
  static setGlobalMetadata(metadata) {
    this.globalMetadata = metadata;
  }

  /**
   * Updates global metadata with provided metadata. Metadata that are provided will just be merged with already set metadata
   */
  static updateGlobalMetadata(metadata) {
    this.globalMetadata = (0, _deepmerge.default)(this.globalMetadata, (0, _cloneDeep.default)(metadata), {
      isMergeableObject: _isPlainObject.isPlainObject
    });
  }
  static isInitialized() {
    return !!this.pinoParent;
  }

  /**
   * Tracks `pageView` manually.
   * Automatic `pageView` tracking in Application Insights is disabled to prevent `pageView` from being sent multiple times.
   * Can be called on the client and Application Insights must be already initialized.
   */
  static trackPageView() {
    if (isInBrowserRuntime && !this.isPageViewTracked && _applicationInsights.ApplicationInsights.isInitialized()) {
      _applicationInsights.ApplicationInsights.getForClient().trackPageView({
        properties: {
          TRACK_THIS_PAGE_VIEW: _constants.TRACK_THIS_PAGE_VIEW
        }
      });
      this.isPageViewTracked = true;
    }
  }

  /** Initializes configuration  */
  static initializeConfiguration() {
    // Initialize configuration for browser
    if (isInBrowserRuntime) {
      // Read initial configuration
      const currentBrowserConfiguration = _LoggerBrowserConfiguration.LoggerBrowserConfiguration.get();
      if (currentBrowserConfiguration) {
        // Set here is kind of intentional. This is so the localStorage is last to be applied
        this.setConfiguration(currentBrowserConfiguration);
      }
      window.addEventListener('storage', this.localStorageListener);
    }
  }
  static withPinoConfigurationNodeTransports(options) {
    // eslint-disable-next-line no-param-reassign -- no need to clone that
    options.transport = {
      pipeline: [{
        target: isTestingLogger ? _nodePath.default.join(__dirname, 'internal/pino-runtime-transports/pinoNodeRuntimeTransport.ts') : require.resolve('./internal/pino-runtime-transports/pinoNodeRuntimeTransport'),
        options: {
          loggerConfiguration: this.getConfiguration(),
          applicationInsightsConfiguration: global.oriAppInsightsSettings
        }
      }]
    };
    return options;
  }
  static withPinoConfigurationBrowserTransports(options) {
    const handler = (0, _pinoBrowserRuntimeTransport.pinoBrowserRuntimeTransport)({
      loggerConfiguration: this.getConfiguration(),
      applicationInsightsConfiguration: global.oriAppInsightsSettings
    });

    // eslint-disable-next-line no-param-reassign -- no need to clone that
    options.browser = {
      write: pinoMessage => handler(pinoMessage)
    };
    return options;
  }

  /** Initilizes Pino */
  static initializeParent() {
    this.initializeConfiguration();
    let pinoOptions = {
      useOnlyCustomLevels: true,
      customLevels: _SeverityLevels.SeverityLevels,
      // IMPORTANT: This has to remain as the lowest possible level in the SeverityLevels enum
      level: 'DEBUG',
      serializers: {
        // This is for environments that uses worker threads - currently only Node.js
        // Worker threads are known to not retain original prototypes and
        // only copies serializable values (anything that is enurable)
        error: _pinoStdSerializers.default.errWithCause
      }
    };
    if (isInNodeRuntime) {
      pinoOptions = this.withPinoConfigurationNodeTransports(pinoOptions);
    } else {
      pinoOptions = this.withPinoConfigurationBrowserTransports(pinoOptions);
    }
    this.pinoParent = (0, _pino.pino)(pinoOptions);
    if (loggerDebugIsEnabled) {
      (0, _printWarningWithTrace.printWarningWithTrace)('Logger was initialized!');
    }

    // Logger reset for tests
    if (isTestingLogger) {
      _loggerEventEmitter.loggerEventEmitter.addListener('reset', () => {
        this.pinoParent = null;
        this._globalMetadata = (0, _cloneDeep.default)(parentLoggerDefaultMessageMetadata);
      });
    }
  }
  initializeChild() {
    const parent = Logger.getPinoParent();
    const setupOptions = {};

    // Name should not part of message during production
    if (isDevelopment) {
      const name = [this.name, this.areaName].filter(Boolean).join(' > ');
      setupOptions.msgPrefix = `[${name}] `;
    }
    if (parent) {
      this.pinoChild = parent.child(
      // due to internal logic we have set this as empty object
      // as we create custom message with properties on each message
      // This is so that we can change properties
      {}, setupOptions);
    }

    // Logger reset for tests
    if (isTestingLogger) {
      _loggerEventEmitter.loggerEventEmitter.addListener('reset', () => {
        this.pinoChild = null;
        // This is so that reset works correctly, normally this.childSetupOptions.bindings won't be used anymore
        // after Logger was initialized
        this.messageMetadata = (0, _cloneDeep.default)(childLoggerDefaultMessageMetadata);
      });
    }
  }
  static getPinoParent() {
    if (!this.isInitialized()) {
      this.initializeParent();
    }
    return this.pinoParent;
  }
  getPinoChild() {
    if (!this.pinoChild) {
      this.initializeChild();
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- should be initialized by now
    return this.pinoChild;
  }
  createPinoMessage(message, additionalMeta) {
    const metadata = (0, _deepmerge.default)(this.messageMetadata, additionalMeta ?? {}, {
      isMergeableObject: _isPlainObject.isPlainObject
    });
    const {
      teamName,
      ...metadataWithoutTeamName
    } = metadata;

    // Ordering matters!
    let result = {
      ...message,
      ...Logger.globalMetadata,
      name: this.name,
      ...(this.areaName && {
        areaName: this.areaName
      }),
      ...(teamName != null && {
        teamName
      }),
      ...(Object.keys(metadataWithoutTeamName).length > 0 && {
        metadata: metadataWithoutTeamName
      })
    };
    if (_applicationInsights.ApplicationInsights.isInitialized() && (0, _applicationInsights.isServerApplicationInsightsClient)(_applicationInsights.ApplicationInsights.get())) {
      const ai = _applicationInsights.ApplicationInsights.getForServer();
      // What a weird typing they have!
      const context = ai.getCorrelationContext();
      const {
        operation
      } = context ?? {};
      const {
        id,
        name,
        parentId
      } = operation ?? {};
      if (operation) {
        result = {
          ...result,
          applicationInsightsOperation: {
            id,
            name,
            parentId
          }
        };
      }
    }
    return result;
  }

  /**
   * Method for logging error
   *
   * @example
   *
   * #### Usage if you want to log error as is
   * ```js
   * const error = new Error('Some error message');
   * new Logger('@ori/awesome').error(error);
   * ```
   *
   * #### Usage if you want to give error additional description
   * ```js
   * const someOtherError = new Error('This is some specific error');
   * const error = new Error(
   *    // Here describe someOtherError in more detail
   *    'Some error message',
   *    {
   *      // You give previous error here
   *      cause: someOtherError,
   *    }
   * );
   * new Logger('@ori/awesome').error(error);
   * ```
   */
  error(error, metadata = {}) {
    const {
      message,
      cause
    } = error;
    const causeMessage =
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- we need to verify runtime value first
    cause && (typeof cause === 'string' || cause instanceof Error) ? cause instanceof Error ? cause.message : cause : undefined;
    const pinoMessage = this.createPinoMessage({
      error: (0, _formatError.formatError)(error)
    }, metadata);
    this.getPinoChild().ERROR(pinoMessage,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need to use || for empty strings
    message || causeMessage || 'An error happened');
  }

  /**
   * Method for logging metric
   *
   * @example Logging the speed of a function call
   * ```js
   *
   * ```
   */
  metric(name, value, metadata = {}) {
    const pinoMessage = this.createPinoMessage({
      metricName: name,
      metricValue: value
    }, metadata);
    this.getPinoChild().METRIC(pinoMessage, `Collected metric with the name "${name}" and value "${value}"`);
  }

  /**
   * Logs info
   */
  info(message, metadata = {}) {
    const pinoMessage = this.createPinoMessage({}, metadata);
    this.getPinoChild().INFO(pinoMessage, message);
  }

  /**
   * Logs debug and is ommited in production mode
   */
  debug(message, metadata = {}) {
    const pinoMessage = this.createPinoMessage({}, metadata);
    this.getPinoChild().DEBUG(pinoMessage, message);
  }

  /**
   * Logs warning
   */
  warn(message, metadata = {}) {
    const pinoMessage = this.createPinoMessage({}, metadata);
    this.getPinoChild().WARN(pinoMessage, message);
  }

  /**
   * Sets new metadata object to this Logger instance.
   */
  setMetadata(metadata) {
    this.getPinoChild().setBindings(metadata);
    return this;
  }

  /**
   * Takes newly provided metadata and merges it with previously defined metadata on this Logger instance
   */
  updateMetadata(metadata) {
    const child = this.getPinoChild();
    this.setMetadata((0, _deepmerge.default)(child.bindings(), metadata, {
      isMergeableObject: _isPlainObject.isPlainObject
    }));
    return this;
  }

  /** Sets configuration for logger */
  static setConfiguration(nextConfiguration) {
    // Merge with default, because the defaults must be always present
    this.configuration = (0, _deepmerge.default)(_constants.defaultLoggerConfiguration, nextConfiguration);
    _loggerEventEmitter.loggerEventEmitter.emit('configurationUpdated', this.configuration);
  }

  /** takes new configuration and merges it with existing */
  static updateConfiguration(nextConfiguration) {
    this.setConfiguration((0, _deepmerge.default)(this.configuration, nextConfiguration));
  }

  /** Gets current configuration for logger */
  static getConfiguration() {
    return this.configuration;
  }
  static isTransportConfigured(transportType) {
    return this.configuration.enabled && this.configuration.filters.some(filter => filter.enabled && filter.byTransportType === transportType);
  }
  static flush() {
    if (isInNodeRuntime) {
      const parent = this.getPinoParent();
      if (parent) {
        parent.flush();
      }
    }
  }
}

// Setup helper Logger methods for browser
exports.Logger = Logger;
_Logger = Logger;
(0, _defineProperty2.default)(Logger, "pinoParent", null);
(0, _defineProperty2.default)(Logger, "isPageViewTracked", false);
(0, _defineProperty2.default)(Logger, "configuration", (0, _cloneDeep.default)(_constants.defaultLoggerConfiguration));
(0, _defineProperty2.default)(Logger, "metadataDependencyInitializer", null);
// backing field for globalMetadata, do not access directly
(0, _defineProperty2.default)(Logger, "_globalMetadata", (0, _cloneDeep.default)(parentLoggerDefaultMessageMetadata));
(0, _defineProperty2.default)(Logger, "localStorageListener", () => {
  const currentBrowserConfiguration = _LoggerBrowserConfiguration.LoggerBrowserConfiguration.get();
  if (currentBrowserConfiguration) {
    _Logger.setConfiguration(currentBrowserConfiguration);
  }
});
if (isInBrowserRuntime) {
  window.ori ??= {};
  window.ori.logger ??= {
    getConfiguration: Logger.getConfiguration,
    setConfiguration: _LoggerBrowserConfiguration.LoggerBrowserConfiguration.set,
    updateConfiguration: _LoggerBrowserConfiguration.LoggerBrowserConfiguration.update
  };
}
//# sourceMappingURL=Logger.js.map